/**
 * Onboarding data model + dynamic interest mapping.
 * Driven entirely by Step 1 ("intents") selections.
 */
import {
    Compass,
    Users,
    Search,
    Briefcase,
    Megaphone,
    MapPin,
    Rocket,
    Sparkles,
    Music2,
    Mic2,
    PartyPopper,
    Flame,
    Dumbbell,
    UtensilsCrossed,
    Palette,
    Camera,
    Brush,
    Wrench,
    Cpu,
    CalendarDays,
    ChefHat,
    Home,
    Network,
    Building2,
    Handshake,
    Trophy,
    Lightbulb,
} from "lucide-react";

export const INTENTS = [
    { id: "discover-events",  label: "Discover events",   icon: Compass,    role: null },
    { id: "join-activities",  label: "Join activities",   icon: Users,      role: null },
    { id: "find-services",    label: "Find services",     icon: Search,     role: null },
    { id: "provide-services", label: "Provide services",  icon: Briefcase,  role: "provider" },
    { id: "host-events",      label: "Host events",       icon: Megaphone,  role: "host" },
    { id: "meet-people",      label: "Meet people nearby", icon: MapPin,    role: null },
    { id: "promote-business", label: "Promote my business", icon: Rocket,   role: "promoter" },
    { id: "others",           label: "Others",            icon: Sparkles,   role: null },
];

/* Interest sets per intent (keep duplicates here; we de-dupe when merging) */
const EVENTS_SET = [
    { id: "afrobeats",   label: "Afrobeats",    icon: Music2 },
    { id: "live-music",  label: "Live Music",   icon: Mic2 },
    { id: "comedy",      label: "Comedy",       icon: Mic2 },
    { id: "parties",     label: "Parties",      icon: PartyPopper },
    { id: "festivals",   label: "Festivals",    icon: Flame },
    { id: "sports",      label: "Sports",       icon: Dumbbell },
    { id: "food",        label: "Food",         icon: UtensilsCrossed },
    { id: "art",         label: "Art",          icon: Palette },
];

const SERVICES_SET = [
    { id: "makeup",       label: "Makeup",              icon: Brush },
    { id: "photography",  label: "Photography",         icon: Camera },
    { id: "cleaning",     label: "Cleaning",            icon: Home },
    { id: "fitness",      label: "Fitness",             icon: Dumbbell },
    { id: "tech",         label: "Tech Services",       icon: Cpu },
    { id: "event-plan",   label: "Event Planning",      icon: CalendarDays },
    { id: "catering",     label: "Catering",            icon: ChefHat },
    { id: "interior",     label: "Interior Decoration", icon: Palette },
];

const PEOPLE_SET = [
    { id: "networking",   label: "Networking",       icon: Network },
    { id: "business",     label: "Business",         icon: Building2 },
    { id: "social",       label: "Social hangouts",  icon: Handshake },
    { id: "sports-grp",   label: "Sports groups",    icon: Trophy },
    { id: "creative",     label: "Creative meetups", icon: Lightbulb },
];

const INTENT_TO_SET = {
    "discover-events":  EVENTS_SET,
    "join-activities":  EVENTS_SET,
    "find-services":    SERVICES_SET,
    "provide-services": SERVICES_SET,
    "host-events":      EVENTS_SET,
    "meet-people":      PEOPLE_SET,
    "promote-business": [...SERVICES_SET, ...PEOPLE_SET],
    "others":           [],
};

/**
 * Build a de-duplicated interests list based on selected intents.
 * When no mappable intents are selected (e.g. only "others"), fall back
 * to a blended starter set so the screen never feels empty.
 */
export const getInterestsForIntents = (intentIds = []) => {
    const pool = [];
    const seen = new Set();
    for (const id of intentIds) {
        const set = INTENT_TO_SET[id] || [];
        for (const item of set) {
            if (!seen.has(item.id)) {
                seen.add(item.id);
                pool.push(item);
            }
        }
    }
    if (pool.length === 0) {
        // user only picked "others" — show a compact blended fallback
        for (const item of [...EVENTS_SET.slice(0, 4), ...PEOPLE_SET.slice(0, 3)]) {
            if (!seen.has(item.id)) {
                seen.add(item.id);
                pool.push(item);
            }
        }
    }
    return pool;
};

/**
 * Map a user's free-text "Other" interest to a canonical Neyra interest bucket.
 * Important: words like "dating", "boyfriend", "girlfriend", "hookup" must NOT
 * be blocked. Neyra is not a dating app — we silently route those to social /
 * meet-people interests instead.
 */
const SOCIAL_REDIRECTS = [
    "date", "dating", "boyfriend", "girlfriend",
    "single", "singles", "hookup", "tinder",
    "romance", "love", "relationship",
];

export const normalizeOtherInterest = (raw) => {
    const txt = String(raw || "").trim().toLowerCase();
    if (!txt) return null;
    if (SOCIAL_REDIRECTS.some((w) => txt.includes(w))) {
        return { id: "social", label: "Social / Hangouts / Meet People", custom: false };
    }
    return { id: `custom-${txt.replace(/\s+/g, "-")}`, label: raw.trim(), custom: true };
};

export const rolesFromIntents = (intentIds = []) => {
    const roles = new Set();
    for (const i of INTENTS) {
        if (i.role && intentIds.includes(i.id)) roles.add(i.role);
    }
    return [...roles];
};
